import { graphql } from 'app/generated/gql'
import type { KollaProvidersEntityOrResourcesEntity } from 'app/services/kolla'
import { Plus } from 'iconoir-react'
import { useMemo, useState } from 'react'
import { useQuery, useMutation } from 'urql'
import { useConfirmation } from '../ConfirmationContext'
import SimpleTable from '../table/SimpleTable'
import { Toggle } from '../Toggle'
import { CreateAvailabilityModal } from './CreateAvailabilityModal'
import { ProviderAvatar } from './ProviderAvatar'
import { StandardModeProviderTypes } from './StandardModeProviderTypes'
import { AppointmentTypeJson } from 'app/types/entities'
import type { AvailabilityType, OpenHours } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useProviderStore } from 'app/stores/StoreContext'
import { availabilityColumns } from 'app/table-columns/availability'

const PROVIDER_AVAILABILITIES_QUERY = graphql(`
  query ProviderAvailabilities($providerId: ID!) {
    providerAvailabilities(providerId: $providerId) {
      id
      orgId
      workspaceId
      locationId
      kollaConsumerId
      providerId
      archived
      operatoryId
      operatoryIds
      appointmentTypeIds
      backupAvailability
      timeSlots {
        sunday {
          hours {
            from
            to
          }
          open
        }
        monday {
          hours {
            from
            to
          }
          open
        }
        tuesday {
          hours {
            from
            to
          }
          open
        }
        wednesday {
          hours {
            from
            to
          }
          open
        }
        thursday {
          hours {
            from
            to
          }
          open
        }
        friday {
          hours {
            from
            to
          }
          open
        }
        saturday {
          hours {
            from
            to
          }
          open
        }
      }
      createdDate
      updatedDate
    }
  }
`)

const DELETE_AVAILABILITY_MUTATION = graphql(`
  mutation DeleteProviderAvailability($id: ID!) {
    deleteProviderAvailability(id: $id)
  }
`)

export default function ProviderPanel({
  provider,
  appointmentTypes,
  operatories,
  onStatusChange,
  isSecondary,
  isV2,
  standardMode,
  locationOpenHours,
  isStandardMode,
}: {
  provider: ProviderType
  appointmentTypes?: AppointmentTypeJson[]
  operatories: KollaProvidersEntityOrResourcesEntity[]
  onStatusChange: (active: boolean) => void
  isSecondary?: boolean
  isV2?: boolean
  standardMode?: boolean
  locationOpenHours?: OpenHours
  isStandardMode?: boolean
}) {
  const [availabilitiesResult, reexecuteAvailabilities] = useQuery({
    query: PROVIDER_AVAILABILITIES_QUERY,
    variables: { providerId: provider.id! },
  })

  const activeAvailabilities = useMemo(
    () =>
      ((availabilitiesResult.data?.providerAvailabilities ?? []) as AvailabilityType[]).filter(
        (availability) => !availability.archived,
      ),
    [availabilitiesResult.data],
  )

  const filteredAvailabilities = useMemo(() => {
    const hits = availabilitiesResult.data?.providerAvailabilities as AvailabilityType[] | undefined
    if (!hits) return []
    return hits
      .map((a) => ({
        ...a,
        appointmentTypeIds: a.appointmentTypeIds?.filter((at) => appointmentTypes?.find((apt) => apt.vendorId === at)),
      }))
      .filter((a) => a.appointmentTypeIds?.length)
  }, [availabilitiesResult.data, appointmentTypes])
  const [confirm] = useConfirmation()
  const [savedName, setSavedName] = useState(provider.displayName!)
  const providerStore = useProviderStore()
  const [, executeDelete] = useMutation(DELETE_AVAILABILITY_MUTATION)

  const onAvailabilityCreated = async () => {
    reexecuteAvailabilities({ requestPolicy: `network-only` })
  }

  const handleDeleteAvailability = async (availabilityId: string) => {
    await confirm({
      title: `Delete availability`,
      description: `Are you sure you want to delete this availability?`,
      variant: `danger`,
    }).then(async () => {
      const result = await executeDelete({ id: availabilityId })
      if (!result.error) {
        closeModal()
        notifySuccess(`Availability deleted`)
        reexecuteAvailabilities({ requestPolicy: `network-only` })
      } else {
        notifyError(`Error deleting availability ${result.error.message}`)
      }
    })
  }

  const openEditAvailabilityModal = (availability: AvailabilityType) => {
    showModal(
      <CreateAvailabilityModal
        onAvailabilityCreatedOrUpdated={onAvailabilityCreated}
        operatories={operatories}
        appointmentTypes={appointmentTypes}
        provider={provider}
        editingAvailability={availability}
        onAvailabilityDelete={handleDeleteAvailability}
        isV2={isV2}
        standardMode={standardMode}
      />,
      {
        title: `Edit availability for ${provider.displayName}`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const openAddAvailabilityModal = () => {
    showModal(
      <CreateAvailabilityModal
        onAvailabilityCreatedOrUpdated={onAvailabilityCreated}
        operatories={operatories}
        appointmentTypes={appointmentTypes}
        provider={provider}
        locationOpenHours={locationOpenHours}
        isV2={isV2}
        standardMode={standardMode}
      />,
      {
        title: `Add availability for ${provider.displayName}`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const onToggleActive = async (active: boolean) => {
    const result = await providerStore.update(provider.id!, { deactivated: !active })
    if (!result.error) {
      notifySuccess(`Provider ${provider.displayName} is now ${active ? `active` : `inactive`}`)
      onStatusChange(active)
    } else {
      notifyError(`Error toggling active state: ${result.error.message}`)
    }
  }

  const saveProvider = async (name: string, priority?: number) => {
    const result = await providerStore.update(provider.id!, { displayName: name, priority })
    if (!result.error) {
      const updated = (result.data as any)?.updateProvider
      if (updated?.displayName) setSavedName(updated.displayName)
    } else {
      notifyError(`Error updating provider: ${result.error.message}`)
    }
  }

  const columns = useMemo(
    () => availabilityColumns(appointmentTypes, operatories, openEditAvailabilityModal, isV2),
    [appointmentTypes, operatories, openEditAvailabilityModal, isV2],
  )

  return (
    <>
      <style>{`
        tr:last-child > td {
          border-bottom-color: white;
        }
      `}</style>
      <div className="flex flex-shrink-0 flex-col items-start rounded-lg border border-default pt-3 shadow-elevation-2">
        <div className="flex items-start self-stretch px-4 pb-3">
          <div className="flex shrink-0 grow basis-0 items-center justify-between">
            <ProviderAvatar
              name={savedName}
              providerId={provider.id!}
              remoteId={provider.vendorId!}
              pmsDisplayName={provider.pmsDisplayName}
              hasPicture={provider.hasProfilePicture}
              priority={provider.priority}
              onSaveProvider={saveProvider}
              isSecondary={isSecondary}
            />

            <div className="flex items-center gap-6">
              {isStandardMode ? null : (
                <button className="btn btn-primary btn-sm" onClick={() => openAddAvailabilityModal()}>
                  <Plus className="h-4 w-4" />
                  <p>Add time</p>
                </button>
              )}
              <Toggle
                label="Active"
                showLabelOnLeft
                defaultValue={!provider.deactivated}
                handleToggle={onToggleActive}
              />
            </div>
          </div>
        </div>
        {isStandardMode && availabilitiesResult.data && (
          <StandardModeProviderTypes
            provider={provider}
            appointmentTypes={appointmentTypes}
            availabilities={activeAvailabilities}
            onSaved={onAvailabilityCreated}
          />
        )}
        {!isStandardMode && !!filteredAvailabilities.length && (
          <div className="mt-4 flex flex-col self-stretch">
            <SimpleTable
              data={filteredAvailabilities}
              columns={columns}
              showColumnOrdering={false}
              noDataMessage="No availabilities found"
              hideFooter={true}
              enableHoverStateStyling={false}
              activeSubpage={provider.id}
            />
          </div>
        )}
      </div>
    </>
  )
}
